import * as React from 'react';
import { DefaultButton, Dropdown, DropdownMenuItemType, Stack, Text } from '@fluentui/react';
import { ArtifactSummary, RuntimeSubstitutionOption } from '../../shared';

interface SnippetToolsProps {
    enabledSnippets: ArtifactSummary[];
    selectedSnippetName?: string;
    onSelectedSnippetNameChange: (snippetName?: string) => void;
    availableSubstitutions: RuntimeSubstitutionOption[];
    selectedSubstitutionToken?: string;
    onSelectedSubstitutionTokenChange: (token?: string) => void;
    selectedInsertionKind: 'snippet' | 'substitution';
    onSelectedInsertionKindChange: (kind: 'snippet' | 'substitution') => void;
    onInsert: (snippetName?: string) => void;
    onInsertSubstitution: (token?: string) => void;
}

export function SnippetTools(props: SnippetToolsProps) {
    const {
        enabledSnippets,
        selectedSnippetName,
        onSelectedSnippetNameChange,
        availableSubstitutions,
        selectedSubstitutionToken,
        onSelectedSubstitutionTokenChange,
        selectedInsertionKind,
        onSelectedInsertionKindChange,
        onInsert,
        onInsertSubstitution,
    } = props;

    const sortedSubstitutions = React.useMemo(
        () => availableSubstitutions.slice().sort((left, right) => left.token.localeCompare(right.token)),
        [availableSubstitutions],
    );
    const sortedSnippets = React.useMemo(
        () => enabledSnippets.slice().sort((left, right) => left.name.localeCompare(right.name)),
        [enabledSnippets],
    );
    const options = React.useMemo(() => {
        const nextOptions: { key: string; text: string; itemType?: DropdownMenuItemType }[] = [];

        if (sortedSubstitutions.length > 0) {
            nextOptions.push({ key: 'substitutions-header', text: 'Dynamic substitutions', itemType: DropdownMenuItemType.Header });
            nextOptions.push(...sortedSubstitutions.map(substitution => ({
                key: `substitution:${substitution.token}`,
                text: substitution.token,
            })));
        }

        if (sortedSubstitutions.length > 0 && sortedSnippets.length > 0) {
            nextOptions.push({ key: 'substitutions-divider', text: '-', itemType: DropdownMenuItemType.Divider });
        }

        if (sortedSnippets.length > 0) {
            nextOptions.push({ key: 'snippets-header', text: 'Snippets', itemType: DropdownMenuItemType.Header });
            nextOptions.push(...sortedSnippets.map(snippet => ({
                key: `snippet:${snippet.name}`,
                text: snippet.name,
            })));
        }

        return nextOptions;
    }, [sortedSnippets, sortedSubstitutions]);
    const selectedKey = selectedInsertionKind === 'snippet'
        ? selectedSnippetName ? `snippet:${selectedSnippetName}` : undefined
        : selectedSubstitutionToken ? `substitution:${selectedSubstitutionToken}` : undefined;
    const selectedSubstitution = React.useMemo(
        () => availableSubstitutions.find(option => option.token === selectedSubstitutionToken),
        [availableSubstitutions, selectedSubstitutionToken],
    );

    return (
        <Stack tokens={{ childrenGap: 8 }}>
            <Stack horizontal wrap tokens={{ childrenGap: 8 }} styles={{ root: { rowGap: 8 } }} verticalAlign="end">
                <div style={{ minWidth: 420, flex: 1 }}>
                    <Dropdown
                        label="Insert snippet or runtime substitution placeholder"
                        options={options}
                        selectedKey={selectedKey}
                        onChange={(_, option) => {
                            const key = option?.key ? String(option.key) : '';
                            if (!key) {
                                return;
                            }

                            if (key.startsWith('substitution:')) {
                                const token = key.slice('substitution:'.length);
                                onSelectedInsertionKindChange('substitution');
                                onSelectedSubstitutionTokenChange(token);
                                onInsertSubstitution(token);
                                return;
                            }

                            if (key.startsWith('snippet:')) {
                                const snippetName = key.slice('snippet:'.length);
                                onSelectedInsertionKindChange('snippet');
                                onSelectedSnippetNameChange(snippetName);
                                onInsert(snippetName);
                            }
                        }}
                        placeholder={options.length > 0 ? 'Choose a snippet or runtime substitution placeholder' : 'No snippets or runtime substitution placeholders available'}
                        disabled={options.length === 0}
                    />
                </div>
                <DefaultButton
                    text="Insert"
                    onClick={() => {
                        if (selectedInsertionKind === 'snippet') {
                            onInsert(selectedSnippetName);
                            return;
                        }

                        onInsertSubstitution(selectedSubstitutionToken);
                    }}
                    disabled={selectedInsertionKind === 'snippet'
                        ? enabledSnippets.length === 0 || !selectedSnippetName
                        : availableSubstitutions.length === 0 || !selectedSubstitutionToken}
                />
            </Stack>
            {selectedInsertionKind === 'substitution' && selectedSubstitution && (
                <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                    {selectedSubstitution.description}
                </Text>
            )}
        </Stack>
    );
}
